import {
  ActionIcon,
  Box,
  Burger,
  Container,
  Flex,
  Image,
  rem,
  Title,
  useMantineColorScheme,
  VisuallyHidden,
} from '@mantine/core';
import { createStyles, getStylesRef } from '@mantine/emotion';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { getLogoSvg } from '../Logo/LogoSvg';

import '@fontsource/agdasima';

import { IconArrowLeft } from '@tabler/icons-react';
import { Link, useNavigate } from 'react-router-dom';
import { ThemeSchemeToggle } from '../ThemeSchemeToggle/ThemeSchemeToggle';
import DrawerCollapse from './DrawerCollapse';

type NavbarProps = {
  isHomepage?: boolean;
};

export default function NavbarEvents({ isHomepage = false }: NavbarProps) {
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
  const { colorScheme } = useMantineColorScheme();
  const { classes, theme } = useStyles();
  const matchesMobile = useMediaQuery('(max-width: 600px)');
  const logoSvg = getLogoSvg();
  const navigate = useNavigate();

  return (
    <Box
      bg={colorScheme === 'dark' ? '#191919' : 'white'}
      sx={{
        width: '100%',
        zIndex: 2,
        boxShadow: theme.shadows.xs,
        justifyContent: 'space-between',
      }}
    >
      <Container size="xl" ff="Agdasima, sans-serif">
        <Flex align="center" justify="space-between">
          <ActionIcon variant="transparent" w="auto" onClick={() => navigate(-1)}>
            <IconArrowLeft color={colorScheme === 'dark' ? 'white' : 'black'} />

            <Title order={4} c={colorScheme === 'dark' ? 'white' : 'black'}>
              VOLTAR
            </Title>
          </ActionIcon>

          <Link to="/" style={{ textDecoration: 'none' }}>
            <Image w={matchesMobile ? 115 : 185} src={logoSvg} alt="Brasil Arena" />
          </Link>

          <Flex h={70}>
            <Flex align="center" gap="xs" sx={{ height: '100%' }} className={classes.hiddenMobile}>
              <ThemeSchemeToggle size={38} />
            </Flex>
          </Flex>
          <Box className={classes.hiddenDesktop}>
            <ThemeSchemeToggle mr={10} size={38} />

            <Burger aria-label="Toggle navigation" opened={drawerOpened} onClick={toggleDrawer}>
              <VisuallyHidden>Toggle navigation</VisuallyHidden>
            </Burger>
          </Box>
        </Flex>
      </Container>

      <DrawerCollapse
        closeDrawer={closeDrawer}
        drawerOpened={drawerOpened}
        isHomepage={isHomepage}
      />
    </Box>
  );
}

const useStyles = createStyles((theme, _, u) => ({
  linkHeader: {
    position: 'relative',
    textDecoration: 'none',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing.lg,
    paddingRight: theme.spacing.lg,
    cursor: 'pointer',
    textTransform: 'uppercase',

    '@media (max-width: 768px)': {
      height: rem(42),
      display: 'flex',
      alignItems: 'center',
      width: '100%',
    },

    '&::before': {
      content: '""',
      position: 'absolute',
      width: '100%',
      height: '4px',
      borderRadius: '4px',
      backgroundColor: theme.colors.principal[5],
      bottom: -5,
      left: '0',
      transformOrigin: 'right',
      transform: 'scaleX(0)',
      transition: 'transform 0.3s ease-in-out',
    },

    '&:hover::before': {
      transformOrigin: 'left',
      transform: 'scaleX(1)',
    },

    '&:hover': {
      backgroundColor: theme.black,

      [`& .${getStylesRef('icon')}`]: {
        color: theme.white,
      },
    },
  },
  linkSubmenu: {
    textDecoration: 'none',
    textTransform: 'uppercase',
    cursor: 'pointer',
    [u.light]: {
      color: theme.black,
    },

    [u.dark]: {
      color: theme.white,
    },
  },

  hiddenMobile: {
    [u.smallerThan('md')]: {
      display: 'none',
    },
  },

  hiddenDesktop: {
    [u.largerThan('md')]: {
      display: 'none',
    },
    display: 'flex',
    alignItems: 'center',
  },
}));
